import React, { useEffect, useMemo, useState, useRef } from "react";
import "./ProviderModal.css";
import ConfirmModal from "./ConfirmModal";

const parseListField = (text) =>
  (text || "")
    .split(/\n|\(\.\)/)
    .map((s) => s.trim())
    .filter(Boolean);

const formatHour = (h) => {
  if (h === null || h === undefined || h === "") return null;
  const s = String(h);
  return s.length >= 5 ? s.slice(0, 5) : s;
};

/* ── Availability helpers ─────────────────────────────────────────────
   Expands booking Schedule_Date rows into per-hour blocks, then for each
   candidate provider checks whether every requested hour:
   (a) is NOT in the provider's busy map for that date, AND
   (b) sits within the provider's opening_hour ≤ h < closing_hour.
*/
const parseHourStr = (hhmm) => {
  if (hhmm == null) return null;
  const s = String(hhmm);
  const h = parseInt(s.split(":")[0], 10);
  return Number.isNaN(h) ? null : h;
};

const expandBookingSlots = (scheduleRows) => {
  // [{day, hour}] for every hour in the booking
  const out = [];
  (scheduleRows || []).forEach((r) => {
    const startH = parseHourStr(r.start_time);
    const hrs = parseInt(r.hours, 10) || 0;
    if (startH == null || hrs <= 0) return;
    const day = String(r.date_day).substring(0, 10);
    for (let i = 0; i < hrs; i++) out.push({ day, hour: startH + i });
  });
  return out;
};

const computeFit = (bookingHours, busyMap) => {
  if (!bookingHours || bookingHours.length === 0) {
    return { status: "no-schedule", conflicts: [] };
  }
  const conflicts = [];
  bookingHours.forEach(({ day, hour }) => {
    // Server may key the map by either "YYYY-MM-DD" or full ISO timestamp.
    // Try both. Also tolerate trailing whitespace / locale variations.
    const candidates = [day];
    Object.keys(busyMap || {}).forEach((k) => {
      if (k.startsWith(day) && !candidates.includes(k)) candidates.push(k);
    });
    let isBusy = false;
    for (const k of candidates) {
      const busyForDay = busyMap?.[k] || [];
      if (
        busyForDay.some(
          (b) => parseHourStr(b.start_time) === hour
        )
      ) {
        isBusy = true;
        break;
      }
    }
    if (isBusy) conflicts.push({ day, hour });
  });
  return {
    status: conflicts.length === 0 ? "available" : "conflict",
    conflicts,
  };
};

const summarizeConflicts = (fit) => {
  if (!fit) return null;
  if (fit.status === "available") return "All requested time slots are free.";
  if (fit.status === "no-schedule") return "No time slots scheduled on this booking yet.";
  const n = fit.conflicts.length;
  return `${n} hour${n > 1 ? "s" : ""} already booked`;
};

/* Booking date range (min → max date) so we can fetch one range request
   per provider instead of one per scheduled day. */
const computeDateRange = (scheduleRows) => {
  if (!scheduleRows || scheduleRows.length === 0) return null;
  const days = scheduleRows
    .map((r) => String(r.date_day).substring(0, 10))
    .filter(Boolean)
    .sort();
  return { start: days[0], end: days[days.length - 1] };
};

/* ── Availability chip component ─────────────────────────────────── */
const AvailabilityChip = ({ fit, loading }) => {
  if (loading) {
    return (
      <span className="pm-avail-chip pm-avail-chip--loading" title="Checking availability…">
        <span className="pm-spinner pm-spinner--xs" /> Checking
      </span>
    );
  }
  if (!fit) return null;
  if (fit.status === "available") {
    return (
      <span className="pm-avail-chip pm-avail-chip--ok" title="No booking conflicts — eligible to assign">
        <i className="bi bi-check-circle-fill" /> Eligible
      </span>
    );
  }
  if (fit.status === "no-schedule") {
    return (
      <span className="pm-avail-chip pm-avail-chip--idle" title="This booking has no scheduled time slots — cannot check conflicts">
        <i className="bi bi-dash-circle" /> No slots
      </span>
    );
  }
  const tip = summarizeConflicts(fit);
  return (
    <span className="pm-avail-chip pm-avail-chip--conflict" title={tip}>
      <i className="bi bi-calendar-x-fill" /> Busy
    </span>
  );
};

const ProviderDetailView = ({
  baseProvider,
  detail,
  loading,
  error,
  onBack,
  onAssign,
  isSelected,
  availability, // { loading, error, fit }
}) => {
  const name = detail?.provider_name || baseProvider?.provider_name;
  const title = detail?.title || baseProvider?.title;
  const initials =
    (name || "")
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "P";

  const opening = formatHour(detail?.opening_hour);
  const closing = formatHour(detail?.closing_hour);
  const hours =
    opening && closing
      ? `${opening} – ${closing}`
      : opening || closing || null;

  const address = [
    detail?.address,
    detail?.city || baseProvider?.city,
    detail?.province,
    detail?.country || baseProvider?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <header className="pm-header pm-header--detail">
        <button
          type="button"
          className="pm-back"
          onClick={onBack}
          aria-label="Back to list"
        >
          <i className="bi bi-arrow-left" />
        </button>
        <div className="pm-header__text">
          <h2 className="pm-title">Provider Details</h2>
        </div>
      </header>

      <div className="pm-detail">
        <div className="pm-detail__hero">
          <div className="pm-detail__avatar">
            {baseProvider?.img_url ? (
              <img src={baseProvider.img_url} alt="" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="pm-detail__hero-text">
            <div className="pm-detail__name">
              {name}
              {detail?.verification === true && (
                <span
                  className="pm-detail__verified"
                  title="Verified provider"
                  aria-label="Verified provider"
                >
                  <i className="bi bi-patch-check-fill" />
                </span>
              )}
            </div>
            {title && <div className="pm-detail__title">{title}</div>}
          </div>
        </div>

        {loading && (
          <div className="pm-detail__loading">
            <div className="pm-spinner" aria-hidden="true" />
            <span>Loading details…</span>
          </div>
        )}

        {error && !loading && (
          <div className="pm-detail__error">
            <i className="bi bi-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && detail && (
          <>
            <section className="pm-section">
              <h3 className="pm-section__heading">Contact</h3>
              <div className="pm-fields">
                {detail.email && (
                  <div className="pm-field">
                    <i className="bi bi-envelope pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Email</div>
                      <a className="pm-field__value pm-field__link" href={`mailto:${detail.email}`}>
                        {detail.email}
                      </a>
                    </div>
                  </div>
                )}
                {detail.phone_number && (
                  <div className="pm-field">
                    <i className="bi bi-telephone pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Phone</div>
                      <a className="pm-field__value pm-field__link" href={`tel:${detail.phone_number}`}>
                        {detail.phone_number}
                      </a>
                    </div>
                  </div>
                )}
                {address && (
                  <div className="pm-field">
                    <i className="bi bi-geo-alt pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Address</div>
                      <div className="pm-field__value">{address}</div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {(detail.experience || detail.year || detail.credentials) && (
              <section className="pm-section">
                <h3 className="pm-section__heading">Experience</h3>
                <div className="pm-fields">
                  {detail.experience && (
                    <div className="pm-field">
                      <i className="bi bi-briefcase pm-field__icon" />
                      <div className="pm-field__body">
                        <div className="pm-field__label">Experience</div>
                        <div className="pm-field__value">{detail.experience}</div>
                      </div>
                    </div>
                  )}
                  {detail.year && (
                    <div className="pm-field">
                      <i className="bi bi-calendar3 pm-field__icon" />
                      <div className="pm-field__body">
                        <div className="pm-field__label">Established</div>
                        <div className="pm-field__value">{detail.year}</div>
                      </div>
                    </div>
                  )}
                  {detail.credentials && (() => {
                    const items = parseListField(detail.credentials);
                    return (
                      <div className="pm-field pm-field--top">
                        <i className="bi bi-award pm-field__icon" />
                        <div className="pm-field__body">
                          <div className="pm-field__label">Credentials</div>
                          {items.length > 0 ? (
                            <ul className="pm-credential-list">
                              {items.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <div className="pm-field__value">—</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </section>
            )}

            {hours && (
              <section className="pm-section">
                <h3 className="pm-section__heading">Hours</h3>
                <div className="pm-fields">
                  <div className="pm-field">
                    <i className="bi bi-clock pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Open</div>
                      <div className="pm-field__value">{hours}</div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {detail.self_description && (() => {
              const lines = parseListField(detail.self_description);
              return (
                <section className="pm-section">
                  <h3 className="pm-section__heading">About</h3>
                  {lines.length > 0 ? (
                    <div className="pm-bio">
                      {lines.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="pm-bio">{detail.self_description}</p>
                  )}
                </section>
              );
            })()}
          </>
        )}
      </div>

      <footer className="pm-footer">
        <button type="button" className="pm-btn pm-btn--ghost" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className={`pm-btn ${availability?.fit?.status === "conflict" ? "pm-btn--warn" : "pm-btn--primary"}`}
          onClick={onAssign}
          disabled={isSelected}
        >
          {isSelected ? (
            <>
              <i className="bi bi-check2" /> Already assigned
            </>
          ) : availability?.fit?.status === "conflict" ? (
            <>
              <i className="bi bi-exclamation-triangle" /> Assign anyway
            </>
          ) : (
            <>
              <i className="bi bi-person-check" /> Assign provider
            </>
          )}
        </button>
      </footer>
    </>
  );
};

export const ProviderModal = ({
  show,
  onClose,
  providers = [],
  selectedProviderId,
  onSelect,
  bookingId,
  bookingLabel,
  onFetchDetail,
  onFetchSchedule,
  onFetchAvailabilityBatch,
}) => {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const [detailProvider, setDetailProvider] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [pendingProviderId, setPendingProviderId] = useState(null);

  // Booking schedule (per-day Schedule_Date rows) — fetched once on open
  const [scheduleRows, setScheduleRows] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  // Tracks whether the schedule fetch has settled (success or fail). The
  // availability effect must wait for this before pre-filling "no-schedule",
  // otherwise the initial empty `bookingHours` poisons the cache and blocks
  // the real fetch from running once scheduleRows finally populates.
  const [scheduleAttempted, setScheduleAttempted] = useState(false);

  // Map of provider_id → { busyMap, fit, loading, error }
  const [availability, setAvailability] = useState({});
  const availFetchSeq = useRef(0); // invalidate stale fetches on re-open

  useEffect(() => {
    if (!show) {
      setSearch("");
      setView("list");
      setDetailProvider(null);
      setDetail(null);
      setDetailError(null);
      setScheduleRows([]);
      setScheduleError(null);
      setScheduleAttempted(false);
      setAvailability({});
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (view === "detail") setView("list");
        else onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [show, onClose, view]);

  // Fetch the booking's schedule rows once when the modal opens.
  useEffect(() => {
    if (!show || !bookingId || !onFetchSchedule) return;
    let cancelled = false;
    setScheduleLoading(true);
    setScheduleError(null);
    setScheduleAttempted(false);
    onFetchSchedule(bookingId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.dates) ? res.data.dates : [];
        setScheduleRows(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ProviderModal: schedule fetch failed", err);
        setScheduleError("Could not load booking schedule. Availability check is unavailable.");
        setScheduleRows([]);
      })
      .finally(() => {
        if (cancelled) return;
        setScheduleLoading(false);
        setScheduleAttempted(true);
      });
    return () => { cancelled = true; };
  }, [show, bookingId, onFetchSchedule]);

  const bookingHours = useMemo(() => expandBookingSlots(scheduleRows), [scheduleRows]);
  const dateRange = useMemo(() => computeDateRange(scheduleRows), [scheduleRows]);

  // ONE batched availability request covers every visible provider.
  // Replaces the old N parallel /providers/availability-range calls.
  useEffect(() => {
    if (!show || !onFetchAvailabilityBatch) return;
    if (scheduleLoading || scheduleError) return;
    // Wait until the schedule fetch has actually settled. Otherwise the
    // initial empty bookingHours would poison the cache as "no-schedule"
    // and prevent the real fetch from running once scheduleRows populates.
    if (bookingId && !scheduleAttempted) return;

    // If booking has no schedule (after fetch settled), mark everyone
    // "no-schedule" without making a request.
    if (!dateRange || bookingHours.length === 0) {
      const next = {};
      providers.forEach((p) => {
        next[p.provider_id] = {
          loading: false,
          error: null,
          fit: { status: "no-schedule", conflicts: [] },
        };
      });
      setAvailability(next);
      return;
    }

    // Only fetch providers we haven't checked yet (cache hits on re-render).
    const todoIds = providers
      .filter((p) => p.provider_id != null && !availability[p.provider_id])
      .map((p) => p.provider_id);
    if (todoIds.length === 0) return;

    const mySeq = ++availFetchSeq.current;

    // Mark all pending providers loading at once.
    setAvailability((prev) => {
      const next = { ...prev };
      todoIds.forEach((id) => {
        next[id] = { loading: true, error: null, fit: null };
      });
      return next;
    });

    onFetchAvailabilityBatch(todoIds, dateRange.start, dateRange.end)
      .then((res) => {
        if (mySeq !== availFetchSeq.current) return; // stale
        const batch = res?.data || {};
        // eslint-disable-next-line no-console
        console.debug("[ProviderModal availability-batch]", {
          range: dateRange,
          bookingHours,
          providerCount: todoIds.length,
          batch,
        });
        setAvailability((prev) => {
          const next = { ...prev };
          todoIds.forEach((id) => {
            const busyMap = batch[id] || batch[String(id)] || {};
            const fit = computeFit(bookingHours, busyMap);
            next[id] = { loading: false, error: null, fit, busyMap };
          });
          return next;
        });
      })
      .catch((err) => {
        if (mySeq !== availFetchSeq.current) return;
        console.error("ProviderModal: batch availability fetch failed", err);
        setAvailability((prev) => {
          const next = { ...prev };
          todoIds.forEach((id) => {
            next[id] = { loading: false, error: "Could not check availability", fit: null };
          });
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, providers, dateRange, bookingHours, scheduleLoading, scheduleError, scheduleAttempted, bookingId, onFetchAvailabilityBatch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((p) => {
      const name = (p.provider_name || "").toLowerCase();
      const title = (p.title || "").toLowerCase();
      const city = (p.city || "").toLowerCase();
      const country = (p.country || "").toLowerCase();
      return (
        name.includes(q) ||
        title.includes(q) ||
        city.includes(q) ||
        country.includes(q)
      );
    });
  }, [providers, search]);

  const openDetail = async (provider) => {
    setDetailProvider(provider);
    setView("detail");
    setDetail(null);
    setDetailError(null);

    if (!onFetchDetail) {
      setDetail(provider);
      return;
    }

    setDetailLoading(true);
    try {
      const res = await onFetchDetail(provider.provider_id);
      const data = res?.data ?? res;
      setDetail(data || provider);
    } catch (err) {
      console.error(err);
      setDetailError("Failed to load provider details.");
      setDetail(provider);
    } finally {
      setDetailLoading(false);
    }
  };

  const backToList = () => {
    setView("list");
    setDetailProvider(null);
    setDetail(null);
    setDetailError(null);
  };

  if (!show) return null;

  const requestPick = (providerId) => setPendingProviderId(providerId);

  const confirmPick = () => {
    onSelect?.(pendingProviderId);
    setPendingProviderId(null);
    onClose?.();
  };

  const cancelPick = () => setPendingProviderId(null);

  const pendingProvider = providers.find((p) => p.provider_id === pendingProviderId);
  const pendingFit = pendingProvider ? availability[pendingProvider.provider_id]?.fit : null;
  const pendingHasConflict = pendingFit?.status === "conflict";

  return (
    <>
    <div className="pm-overlay" role="dialog" aria-modal="true" aria-labelledby="pm-title">
      <div className="pm-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="pm-dialog" role="document">
        {view === "detail" ? (
          <ProviderDetailView
            baseProvider={detailProvider}
            detail={detail}
            loading={detailLoading}
            error={detailError}
            onBack={backToList}
            onAssign={() =>
              detailProvider && requestPick(detailProvider.provider_id)
            }
            isSelected={detailProvider?.provider_id === selectedProviderId}
            availability={detailProvider ? availability[detailProvider.provider_id] : null}
          />
        ) : (
          <>
            <header className="pm-header">
              <div className="pm-header__text">
                <h2 id="pm-title" className="pm-title">
                  <i className="bi bi-person-badge pm-title__icon" />
                  Assign Provider
                </h2>
                {bookingLabel && (
                  <p className="pm-subtitle">For booking {bookingLabel}</p>
                )}
              </div>
              <button
                type="button"
                className="pm-close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div className="pm-search">
              <i className="bi bi-search pm-search__icon" aria-hidden="true" />
              <input
                type="text"
                className="pm-search__input"
                placeholder="Search by name, title, or location…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  className="pm-search__clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x" />
                </button>
              )}
            </div>

            <div className="pm-meta">
              <span className="pm-count">
                <strong>{filtered.length}</strong> of {providers.length} verified
              </span>
              {scheduleLoading && (
                <span className="pm-meta__hint">
                  <span className="pm-spinner pm-spinner--xs" /> Loading booking schedule…
                </span>
              )}
              {!scheduleLoading && scheduleError && (
                <span className="pm-meta__hint pm-meta__hint--error">
                  <i className="bi bi-exclamation-triangle" /> {scheduleError}
                </span>
              )}
              {!scheduleLoading && !scheduleError && bookingHours.length > 0 && (
                <span className="pm-meta__hint">
                  <i className="bi bi-calendar-check" />
                  Checking against {bookingHours.length} hour{bookingHours.length > 1 ? "s" : ""}
                  {" · "}
                  {dateRange?.start === dateRange?.end
                    ? dateRange?.start
                    : `${dateRange?.start} → ${dateRange?.end}`}
                </span>
              )}
              {!scheduleLoading && !scheduleError && bookingHours.length === 0 && bookingId && (
                <span className="pm-meta__hint">
                  <i className="bi bi-info-circle" /> Booking has no scheduled slots — no conflict check
                </span>
              )}
            </div>

            <div className="pm-list" role="listbox" aria-label="Available providers">
              {filtered.length === 0 ? (
                <div className="pm-empty">
                  <div className="pm-empty__icon">
                    <i className="bi bi-person-x" />
                  </div>
                  <h4>No providers match</h4>
                  <p>
                    {providers.length === 0
                      ? "No verified nearby providers for this booking."
                      : "Try a different search term."}
                  </p>
                </div>
              ) : (
                filtered.map((p) => {
                  const isSelected = p.provider_id === selectedProviderId;
                  const initials =
                    (p.provider_name || "")
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase() || "P";

                  const availEntry = availability[p.provider_id];
                  const fit = availEntry?.fit;
                  const availLoading = availEntry?.loading;
                  const isConflict = fit?.status === "conflict";

                  return (
                    <div
                      key={p.provider_id}
                      role="option"
                      aria-selected={isSelected}
                      className={`pm-row ${isSelected ? "pm-row--selected" : ""} ${isConflict ? "pm-row--conflict" : ""}`}
                    >
                      <button
                        type="button"
                        className="pm-row__main"
                        onClick={() => requestPick(p.provider_id)}
                        aria-label={`Assign ${p.provider_name}${isConflict ? " (has schedule conflict)" : ""}`}
                      >
                        <div className="pm-row__avatar">
                          {p.img_url ? (
                            <img
                              src={p.img_url}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span>{initials}</span>
                          )}
                        </div>
                        <div className="pm-row__body">
                          <div className="pm-row__name">
                            {p.provider_name}
                            {isSelected && (
                              <span className="pm-row__badge">Current</span>
                            )}
                            <AvailabilityChip fit={fit} loading={availLoading} />
                          </div>
                          <div className="pm-row__meta">
                            <span className="pm-row__title">{p.title || "—"}</span>
                            {(p.city || p.country) && (
                              <>
                                <span className="pm-row__sep">·</span>
                                <span className="pm-row__loc">
                                  <i className="bi bi-geo-alt" />
                                  {[p.city, p.country].filter(Boolean).join(", ")}
                                </span>
                              </>
                            )}
                            {p.experience && (
                              <>
                                <span className="pm-row__sep">·</span>
                                <span className="pm-row__exp">
                                  <i className="bi bi-briefcase" />
                                  {p.experience}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        className="pm-row__info"
                        onClick={() => openDetail(p)}
                        title="View details"
                        aria-label={`View details for ${p.provider_name}`}
                      >
                        <i className="bi bi-info-circle" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <footer className="pm-footer">
              <button type="button" className="pm-btn pm-btn--ghost" onClick={onClose}>
                Cancel
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
    <ConfirmModal
      show={!!pendingProviderId}
      onConfirm={confirmPick}
      onCancel={cancelPick}
      title={pendingHasConflict ? "Schedule conflict — assign anyway?" : "Assign provider?"}
      message={
        pendingProvider
          ? pendingHasConflict
            ? `${pendingProvider.provider_name} is unavailable for some of this booking's time slots (${summarizeConflicts(pendingFit)}). Assign anyway?`
            : `Assign ${pendingProvider.provider_name} to this booking?`
          : undefined
      }
      confirmLabel={pendingHasConflict ? "Assign anyway" : "Assign"}
      variant={pendingHasConflict ? "danger" : "accent"}
    />
    </>
  );
};

export default ProviderModal;
